import React, { useEffect, useState } from 'react';
import AddDryingCheckDrawer from '@/components/drying-lots/AddDryingCheckDrawer';
import DryingBedCheckItem from '@/components/drying-lots/DryingBedCheckItem';
import { Button } from '@/components/shadcn-ui/button';
import { Card } from '@/components/shadcn-ui/card';
import { endDryingOnBed, getDryingBedChecks, getDryingBedsOfLot } from '@/network/api.service';
import { useLotStore } from '@/stores/useLot.store';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDateTimeUTC } from './DryingInProgressPage';
import { useMasterStore } from '@/stores/useMaster.store';
import { Printer } from 'lucide-react';
import { generateBedPdf } from '@/lib/generate-bed-pdf';

const formatBedCode = (bed_number, bed_position) => {
  const prefix = bed_position === 'upper' ? 'U' : bed_position === 'middle' ? 'M' : 'L';
  return `${prefix}${String(bed_number).padStart(2, '0')}`;
};

const DryingLotBedActivePage = () => {
  const [loading, setLoading] = useState(false);
  const [bedChecks, setBedChecks] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [bedsData, setBedsData] = useState([]);

  const selectedDryingBed = useLotStore(state => state.selectedDryingBed);
  const { lot_id, drying_lot_id } = useParams();
  const fetchLot = useLotStore(state => state.fetchLot);
  const lot = useLotStore(state => state.lot);
  const navigate = useNavigate();
  const fetchDryingBeds = useMasterStore(state => state.fetchDryingBeds);

  useEffect(() => {
    const fetchLotData = async () => {
      await fetchLot(lot_id, false);
      const { data } = await getDryingBedChecks(drying_lot_id);
      useLotStore.getState().fetchDryingBedInfo(drying_lot_id);
      const { data: dryingBedsData } = await getDryingBedsOfLot(lot_id);

      setBedsData(dryingBedsData?.data);
      setBedChecks(data?.data);
    };

    fetchLotData();
  }, [lot_id]);

  const handleEditClick = item => {
    setEditingItem(item);
    setDrawerOpen(true);
  };

  const handleDrawerClose = React.useCallback(
    (shouldRefresh = false) => {
      setDrawerOpen(false);
      setEditingItem(null);

      // Only refresh bed checks if data was saved
      if (shouldRefresh) {
        const fetchLotData = async () => {
          const { data } = await getDryingBedChecks(drying_lot_id);
          setBedChecks(data?.data);
        };
        fetchLotData();
      }
    },
    [drying_lot_id]
  );

  function getActiveBedLabelsSorted(data) {
    const positionOrder = {
      upper: 1,
      middle: 2,
      lower: 3,
    };

    const positionPrefix = {
      upper: 'U',
      middle: 'M',
      lower: 'L',
    };

    return data
      .filter(bed => bed.drying_status === true)
      .sort((a, b) => {
        // sort by position first
        if (positionOrder[a.bed_position] !== positionOrder[b.bed_position]) {
          return positionOrder[a.bed_position] - positionOrder[b.bed_position];
        }
        // then by bed number
        return a.bed_number - b.bed_number;
      })
      .map(bed => {
        const prefix = positionPrefix[bed.bed_position];
        const number = String(bed.bed_number).padStart(2, '0');
        return prefix + number;
      });
  }
  const handleGenerateBedPdf = async () => {
    const activeBedLabels = getActiveBedLabelsSorted(bedsData);
    await generateBedPdf({
      lot_name: lot?.lotname,
      estate_name: lot?.estate?.name,
      harvest_date: lot?.date_of_harvest,
      drying_start_date: lot?.drying_lot?.drying_start_time,
      bed_label: formatBedCode(selectedDryingBed?.bed_number, selectedDryingBed?.bed_position),
      active_beds: activeBedLabels,
      bed_number: selectedDryingBed?.bed_number,
      bed_position: selectedDryingBed?.bed_position,
    });
  };

  const handleEndDryingOnBed = async () => {
    try {
      const { data } = await endDryingOnBed({ dryingBedId: drying_lot_id, data: { drying_end_time: new Date() } });
      if (data?.success) {
        toast.success('Drying on bed ended successfully');
        navigate(`/drying-lots/in-progress/${lot_id}`, { replace: true });
        await fetchDryingBeds();
      }
    } catch (error) {
      toast.error('Failed to end drying on bed');
    }
  };

  return (
    <div className="flex h-full flex-1 flex-col space-y-[18px] px-4 py-[18px] pb-2">
      {/* Lot Information */}
      <Card className="flex flex-col gap-1 p-4">
        <div className="flex items-center justify-between">
          <div className="mb-1 text-base font-semibold">Lot Information</div>
          <button className="mr-1" onClick={handleGenerateBedPdf}>
            <Printer size={20} />
          </button>
        </div>
        <div className="text-sm">
          <span className="font-semibold">Lot:</span> {lot?.lotname}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Estate:</span> {lot?.estate?.name}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Drying start date:</span>{' '}
          {formatDateTimeUTC(lot?.drying_lot?.drying_start_time || new Date())}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Drying start weight:</span> {lot?.drying_lot?.drying_start_weight_kg} kg
        </div>
      </Card>

      {/* Drying Section */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Scrollable List ONLY */}
        <div className="flex items-center justify-between">
          <div className="font-medium">
            Bed&nbsp;
            {formatBedCode(selectedDryingBed?.bed_number, selectedDryingBed?.bed_position)}
          </div>
        </div>
        <div className="mt-3 flex-1 space-y-3 overflow-y-auto">
          {bedChecks?.map(item => (
            <DryingBedCheckItem key={item?._id} item={item} onEditClick={handleEditClick} />
          ))}
        </div>

        {/* Fixed Bottom Button */}
        <div className="flex flex-col gap-4">
          <AddDryingCheckDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            onClose={handleDrawerClose}
            initialData={editingItem}
            setEditingItem={setEditingItem}
          />
          <Button disabled={selectedDryingBed?.drying_end_time || bedChecks?.length < 1} onClick={handleEndDryingOnBed}>
            End drying on bed
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DryingLotBedActivePage;
